import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Building2, Check, ChevronDown, ClipboardList, Euro, Mail, MapPin } from "lucide-react";
import { TYPES, STAGES, ANALYSIS_STEPS, TYPES_PT, STAGES_PT, ANALYSIS_STEPS_PT, TYPES_NL, STAGES_NL, ANALYSIS_STEPS_NL } from "./IndicativeTerms";
import { useLang } from "../i18n/LangContext";

const fieldBox =
  "flex items-center gap-3 rounded-xl border border-sand bg-cream/60 px-4 py-2.5 transition-colors focus-within:border-verde-700 focus-within:bg-white";
const fieldLabel = "block text-[11px] font-medium text-fog";
const fieldControl = "w-full bg-transparent text-base font-medium outline-none placeholder:text-fog/60";

export default function HeroIntakeForm() {
  const navigate = useNavigate();
  const { lang, lp } = useLang();
  const T = {
    en: {
      title: "Get indicative terms",
      sub: "Within 24 hours we send terms. Close within one week.",
      location: "Project location",
      locationPh: "e.g. Cascais, Portugal",
      type: "Project type",
      typePh: "Select project type",
      stage: "Project stage",
      stagePh: "Select stage",
      amount: "Financing required",
      amountPh: "e.g. 2,000,000",
      submit: "Continue",
      emailTitle: "Where should we send your indicative terms?",
      emailLabel: "Your email",
      emailPh: "e.g. you@company.pt",
      emailErr: "Please enter a valid email address.",
      submit2: "Start my case",
      back: "Edit project details",
      note: "No obligation. All financing subject to review and approval.",
      preparing: "Preparing your case…",
      types: TYPES,
      stages: STAGES,
      steps: ANALYSIS_STEPS,
    },
    pt: {
      title: "Obtenha condições indicativas",
      sub: "Em 24 horas enviamos as condições. Fecho numa semana.",
      location: "Localização do projeto",
      locationPh: "ex.: Cascais, Portugal",
      type: "Tipo de projeto",
      typePh: "Selecione o tipo de projeto",
      stage: "Fase do projeto",
      stagePh: "Selecione a fase",
      amount: "Financiamento necessário",
      amountPh: "ex.: 2.000.000",
      submit: "Continuar",
      emailTitle: "Para onde enviamos as suas condições indicativas?",
      emailLabel: "O seu email",
      emailPh: "ex.: nome@empresa.pt",
      emailErr: "Introduza um endereço de email válido.",
      submit2: "Iniciar o meu processo",
      back: "Editar detalhes do projeto",
      note: "Sem compromisso. Todo o financiamento está sujeito a análise e aprovação.",
      preparing: "A preparar o seu processo…",
      types: TYPES_PT,
      stages: STAGES_PT,
      steps: ANALYSIS_STEPS_PT,
    },
    nl: {
      title: "Indicatieve voorwaarden",
      sub: "Binnen 24 uur sturen wij voorwaarden. Sluit binnen een week.",
      location: "Projectlocatie",
      locationPh: "bijv. Cascais, Portugal",
      type: "Projecttype",
      typePh: "Kies een projecttype",
      stage: "Projectfase",
      stagePh: "Kies een fase",
      amount: "Benodigde financiering",
      amountPh: "bijv. 2.000.000",
      submit: "Verder",
      emailTitle: "Waar mogen wij uw indicatieve voorwaarden naartoe sturen?",
      emailLabel: "Uw e-mailadres",
      emailPh: "bijv. naam@bedrijf.pt",
      emailErr: "Voer een geldig e-mailadres in.",
      submit2: "Start mijn dossier",
      back: "Projectgegevens bewerken",
      note: "Geen verplichtingen. Alle financiering is onder voorbehoud van beoordeling en goedkeuring.",
      preparing: "Uw dossier wordt voorbereid…",
      types: TYPES_NL,
      stages: STAGES_NL,
      steps: ANALYSIS_STEPS_NL,
    },
  }[lang];
  const [location, setLocation] = useState("");
  const [type, setType] = useState("");
  const [stage, setStage] = useState("");
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [formStep, setFormStep] = useState("details"); // details | email | preparing
  const [step, setStep] = useState(-1);
  const timer = useRef(null);

  const ready = type && amount.trim() && stage && location.trim();
  const emailOk = /.+@.+\..+/.test(email);
  const [nudge, setNudge] = useState(false);

  useEffect(() => () => clearInterval(timer.current), []);

  const start = (e) => {
    e.preventDefault();
    if (formStep !== "details") return;
    if (!ready) {
      setNudge(true);
      return;
    }
    setNudge(false);
    setFormStep("email");
  };

  const startCase = (e) => {
    e.preventDefault();
    if (!emailOk || step >= 0) return;
    setFormStep("preparing");
    setStep(0);
    let i = 0;
    timer.current = setInterval(() => {
      i += 1;
      if (i >= T.steps.length) {
        clearInterval(timer.current);
        navigate(lp("/apply"), {
          state: {
            prefill: { location: location.trim(), type, stage, amount: amount.trim(), email: email.trim() },
          },
        });
      } else {
        setStep(i);
      }
    }, 700);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
      className="w-full max-w-[480px] rounded-2xl bg-white p-6 sm:p-8 shadow-[0_40px_90px_-30px_rgba(7,39,29,0.45)]"
      data-testid="hero-intake-form"
    >
      {formStep === "preparing" ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-3">
          <p className="font-display text-lg font-bold text-charcoal">{T.preparing}</p>
          {T.steps.map((label, i) => (
            <div key={label} className="flex items-center justify-between border-b border-sand/70 pb-2.5" data-testid={`hero-form-step-${i}`}>
              <span className={`text-xs font-medium ${i <= step ? "text-verde-800" : "text-fog/50"}`}>{label}</span>
              {i < step ? (
                <Check className="h-3.5 w-3.5 text-mint" strokeWidth={3} />
              ) : i === step ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-verde-700 border-t-transparent" />
              ) : (
                <span className="h-3.5 w-3.5 rounded-full border border-sand" />
              )}
            </div>
          ))}
          <div className="h-1 w-full overflow-hidden rounded-full bg-cream-dark">
            <motion.div
              className="h-full rounded-full bg-verde-800"
              animate={{ width: `${((step + 1) / ANALYSIS_STEPS.length) * 100}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
        </motion.div>
      ) : (
        <>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-charcoal">
            {formStep === "email" ? T.emailTitle : T.title}
          </h2>
          {formStep === "details" && (
            <p className="mt-1.5 text-[13px] leading-relaxed text-fog">
              {T.sub}
            </p>
          )}

          {formStep === "details" ? (
            <motion.form onSubmit={start} className="mt-5 space-y-3" data-testid="hero-form-details">
              <label className={`${fieldBox} border-verde-600/60 bg-white ${nudge && !location.trim() ? "border-red-400 ring-2 ring-red-200" : ""}`}>
                <MapPin className="h-4.5 w-4.5 shrink-0 text-verde-700" strokeWidth={1.8} />
                <span className="min-w-0 flex-1">
                  <span className={fieldLabel}>{T.location}</span>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={T.locationPh}
                    data-testid="hero-form-location"
                    className={`${fieldControl} text-charcoal`}
                  />
                </span>
              </label>

              <label className={`${fieldBox} ${nudge && !type ? "border-red-400 ring-2 ring-red-200" : ""}`}>
                <Building2 className="h-4.5 w-4.5 shrink-0 text-verde-700" strokeWidth={1.8} />
                <span className="min-w-0 flex-1">
                  <span className={fieldLabel}>{T.type}</span>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    data-testid="hero-form-type"
                    className={`${fieldControl} cursor-pointer appearance-none ${type ? "text-charcoal" : "text-fog/70"}`}
                  >
                    <option value="" disabled>{T.typePh}</option>
                    {T.types.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-fog" />
              </label>

              <label className={`${fieldBox} ${nudge && !stage ? "border-red-400 ring-2 ring-red-200" : ""}`}>
                <ClipboardList className="h-4.5 w-4.5 shrink-0 text-verde-700" strokeWidth={1.8} />
                <span className="min-w-0 flex-1">
                  <span className={fieldLabel}>{T.stage}</span>
                  <select
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    data-testid="hero-form-stage"
                    className={`${fieldControl} cursor-pointer appearance-none ${stage ? "text-charcoal" : "text-fog/70"}`}
                  >
                    <option value="" disabled>{T.stagePh}</option>
                    {T.stages.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-fog" />
              </label>

              <label className={`${fieldBox} ${nudge && !amount.trim() ? "border-red-400 ring-2 ring-red-200" : ""}`}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-sand">
                  <Euro className="h-3 w-3 text-verde-700" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={fieldLabel}>{T.amount}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={T.amountPh}
                    data-testid="hero-form-amount"
                    className={`${fieldControl} text-charcoal`}
                  />
                </span>
              </label>

              <button
                type="submit"
                data-testid="hero-form-submit"
                className="group flex w-full items-center justify-center gap-2.5 rounded-xl bg-verde-950 px-6 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-verde-800"
              >
                {T.submit}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="text-center text-[11px] text-fog">{T.note}</p>
            </motion.form>
          ) : (
            <motion.form
              onSubmit={startCase}
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 space-y-3"
              data-testid="hero-form-email-step"
            >
              <label className={`${fieldBox} border-verde-600/60 bg-white`}>
                <Mail className="h-4.5 w-4.5 shrink-0 text-verde-700" strokeWidth={1.8} />
                <span className="min-w-0 flex-1">
                  <span className={fieldLabel}>{T.emailLabel}</span>
                  <input
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={T.emailPh}
                    data-testid="hero-email-input"
                    className={`${fieldControl} text-charcoal`}
                  />
                </span>
              </label>
              {email.trim() && !emailOk && (
                <p className="text-xs text-red-600" data-testid="hero-email-error">{T.emailErr}</p>
              )}
              <button
                type="submit"
                disabled={!emailOk}
                data-testid="hero-form-submit-email"
                className="group flex w-full items-center justify-center gap-2.5 rounded-xl bg-verde-950 px-6 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-verde-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {T.submit2}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                onClick={() => setFormStep("details")}
                data-testid="hero-form-back"
                className="mx-auto flex items-center gap-1.5 text-xs font-medium text-fog transition-colors hover:text-verde-800"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {T.back}
              </button>
              <p className="text-center text-[11px] text-fog">{T.note}</p>
            </motion.form>
          )}
        </>
      )}
    </motion.div>
  );
}
